'use client';

import { isInternalServerError } from '@payments-checkout/shared/helpers/constants/http-status.const';
import { analiticaInstance } from '@payments-checkout/shared/plugins/analitica/analitica.events';
import {
  reportError,
  trackAttempt,
} from '@payments-checkout/shared/observability';
import { usePaymentMethod } from '@payments-checkout/shared/providers';
import { useRouter } from 'next/navigation';
import { useMemo } from 'react';
import { errorMessagePaymentGeneric } from '../../components/pages/cardAddress/errorMessage';
import { usePageSharedData } from '../../hooks/usePageSharedData';
import { buildPaymentPayload, usePayment } from '../../hooks/usePayment';
import { buildPixSteps, normalizePixFlow } from './pixFlow';
import { PixAddressStep, PixStep } from './pixFlow.types';

const PIX_STEP_ROUTES: Record<PixAddressStep, string> = {
  'billing-address': 'card-data',
  'shipping-address': 'delivery-address',
};

export function usePixFlow() {
  const { push } = useRouter();
  const { paymentLinkId, paymentLinkIdData } = usePageSharedData();
  const {
    setShowFailedPaymentAlert,
    setFailedPaymentMessage,
    setIsCustomerFormLoading,
  } = usePaymentMethod();

  const pixFlow = normalizePixFlow(
    paymentLinkIdData.data?.flow_settings?.pix_flow,
    paymentLinkIdData.data?.flow_settings?.show_shipping_address_page
  );
  const steps = useMemo(() => buildPixSteps(pixFlow), [pixFlow]);

  const { mutate: sendPayment, isPending: isPaymentPending } = usePayment({
    onError: (error) => {
      const errorStatus = error?.response?.status ?? 500;
      reportError(error, { source: 'payment_submit', payment_method: 'pix' });
      analiticaInstance.events.homePage.paymentErrorViewed(String(errorStatus));
      setShowFailedPaymentAlert(true);
      setFailedPaymentMessage(
        isInternalServerError(errorStatus)
          ? errorMessagePaymentGeneric(1002)
          : errorMessagePaymentGeneric(1000)
      );
      setIsCustomerFormLoading(false);
    },
  });

  const payPix = () => {
    trackAttempt('payment_submit');
    return sendPayment({ paymentData: buildPaymentPayload('pix') });
  };

  const advanceTo = (nextStep?: PixStep) => {
    if (!nextStep || nextStep === 'payment') {
      payPix();
      return;
    }
    push(`/${paymentLinkId}/${PIX_STEP_ROUTES[nextStep]}`);
  };

  const goToFirstStep = () => advanceTo(steps[0]);

  return {
    steps,
    isReducedPix: steps.length === 1,
    goToFirstStep,
    payPix,
    isPaymentPending,
  };
}
